<template>
  <v-card :loading="loading" :disabled="loading">
    <v-card-title class="flex-wrap">
      <span>{{ title }}</span>
      <v-spacer />
      <v-btn
        v-if="can_view_student_notes && student_data.level_id"
        color="#ff5722"
        outlined
        class="my-1"
        @click="student_note_dialog = true"
      >
        <v-icon left>
          {{ can_manage_student_notes ? "mdi-note-edit-outline" : "mdi-note-text-outline" }}
        </v-icon>
        {{ $t("payment.student_notes") }}
      </v-btn>
    </v-card-title>

    <slot name="controls"></slot>

    <v-card-text v-if="student_id" class="pa-0">
      <v-alert
        v-if="load_error"
        class="mx-4"
        color="error"
        icon="mdi-alert-circle-outline"
        outlined
        text
      >
        {{
          typeof load_error === "object"
            ? objectTranslatorLowerCase(load_error, "message")
            : load_error
        }}
      </v-alert>

      <template v-if="student_data.level_id">
        <v-row
          dense
          class="student-payment-summary grey lighten-2 mb-2 mx-auto"
        >
          <v-col cols="12" class="py-0">
            <div class="student-summary-grid">
              <div class="student-summary-item">
                <v-icon small color="grey darken-2">mdi-card-account-details-outline</v-icon>
                <span class="student-summary-label">
                  {{ $t("pre_regis_panel.student_id") }}:
                </span>
                <span>{{ student_id }}</span>
              </div>
              <div class="student-summary-item">
                <v-icon small color="grey darken-2">mdi-account-outline</v-icon>
                <span class="student-summary-label">
                  {{ $t("pre_regis_panel.name_surname") }}:
                </span>
                <span>
                  {{ inlineTranslator({ en: student_name_en, th: student_name_th }) }}
                </span>
              </div>
              <div class="student-summary-item">
                <v-icon small color="grey darken-2">mdi-school-outline</v-icon>
                <span class="student-summary-label">
                  {{ $t("payment.education_level") }}:
                </span>
                <span>
                  {{
                    inlineTranslator({
                      en: student_data.level_name_en,
                      th: student_data.level_name_th,
                    })
                  }}
                </span>
              </div>
              <div class="student-summary-item">
                <v-icon small color="grey darken-2">mdi-domain</v-icon>
                <span class="student-summary-label">
                  {{ $t("payment.faculty_college_campus") }}:
                </span>
                <span>
                  <span v-if="faculty_prefix">{{ faculty_prefix }}</span>
                  {{
                    inlineTranslator({
                      en: student_data.faculty_ename || "",
                      th: student_data.faculty_name,
                    })
                  }}
                </span>
              </div>
            </div>
          </v-col>
        </v-row>

        <PaymentTable
          v-if="show_undergraduate"
          :loading="loading"
          :transaction_list="transaction_list"
          :student_id="String(student_id)"
          :student_name="student_display_name"
          :level_id="1"
          :sub_level_id="null"
          :student_mode="student_mode"
          :editable="editable"
          :fee_editable="can_edit_payment_fees"
          :payment_actions_enabled="can_use_payment_actions"
          @updated="refresh_data"
        />

        <v-divider v-if="show_undergraduate && show_graduate"></v-divider>

        <PaymentTable
          v-if="show_graduate"
          :loading="loading"
          :transaction_list="transaction_list_x"
          :student_id="String(student_id)"
          :student_name="student_display_name"
          :level_id="level_id"
          :sub_level_id="sub_level_id"
          :student_mode="student_mode"
          :editable="editable"
          :fee_editable="can_edit_payment_fees"
          :payment_actions_enabled="can_use_payment_actions"
          @updated="refresh_data"
        />
      </template>
    </v-card-text>

    <PaymentStudentNoteDialog
      v-if="can_view_student_notes"
      v-model="student_note_dialog"
      :student_id="String(student_id || '')"
      :student_name="student_display_name"
      :readonly="!can_manage_student_notes"
    />
  </v-card>
</template>

<script>
import payInSlipProvider from "@/services/pay_in_slip.provider";
import UserProvider from "@/services/user.provider";
import { getById } from "@/helper/filter";
import {
  inlineTranslator,
  objectTranslatorLowerCase,
} from "@/helper/translator";
import PaymentTable from "./PaymentTable.vue";
import PaymentStudentNoteDialog from "./PaymentStudentNoteDialog.vue";

const payInSlipService = new payInSlipProvider();
const userService = new UserProvider();

export default {
  name: "PaymentPanel",
  components: {
    PaymentTable,
    PaymentStudentNoteDialog,
  },
  props: {
    student_id: {
      type: [String, Number],
      default: null,
    },
    title: {
      type: String,
      required: true,
    },
    student_mode: {
      type: Boolean,
      default: false,
    },
    editable: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    loading: false,
    load_error: null,
    request_id: 0,
    transaction_list: [],
    transaction_list_x: [],
    student_data: {},
    level_id: null,
    sub_level_id: null,
    student_note_dialog: false,
    type_of_regis: [
      {
        regis_type: "S",
        regis_type_txt_th: "ลงทะเบียนปกติ",
        regis_type_txt_en: "Registration",
      },
      {
        regis_type: "M",
        regis_type_txt_th: "รักษาสถานภาพนักศึกษา",
        regis_type_txt_en: "Maintain",
      },
      {
        regis_type: "D",
        regis_type_txt_th: "ลาพักการศึกษา",
        regis_type_txt_en: "Leave of absent",
      },
    ],
  }),
  computed: {
    current_privilege() {
      return Number(
        this.$store.state.user.user_info?.payload?.ticket?.privilege
      );
    },
    can_edit_payment_fees() {
      return this.editable && [91, 40, 45].includes(this.current_privilege);
    },
    can_view_student_notes() {
      return (
        this.editable &&
        [91, 40, 45].includes(this.current_privilege)
      );
    },
    can_manage_student_notes() {
      return this.editable && [91, 40, 45].includes(this.current_privilege);
    },
    can_use_payment_actions() {
      // หน้านักศึกษายังคงชำระได้ตามปกติ แต่เจ้าหน้าที่คณะ (10) ดูข้อมูลอย่างเดียว
      return !this.editable || [91, 40, 45, 30].includes(this.current_privilege);
    },
    use_student_payment_condition() {
      // เจ้าหน้าที่คณะและเจ้าหน้าที่การเงินต้องเห็นผลการตรวจช่วงชำระเงิน
      // เหมือนที่นักศึกษาเห็น แม้กำลังเปิดข้อมูลผ่านหน้า /payment_officer
      return (
        this.student_mode ||
        (this.editable && [10, 30].includes(this.current_privilege))
      );
    },
    should_check_payment_condition() {
      return this.can_use_payment_actions || this.use_student_payment_condition;
    },
    show_undergraduate() {
      return this.level_id === 1;
    },
    show_graduate() {
      return (
        (this.level_id === 1 && this.sub_level_id === 1) ||
        this.level_id === 2
      );
    },
    student_name_th() {
      return [
        this.student_data.prename_th,
        this.student_data.name_th,
        this.student_data.surname_th,
      ]
        .filter(Boolean)
        .join(" ");
    },
    student_name_en() {
      return [
        this.student_data.prename_en,
        this.student_data.name_en,
        this.student_data.surname_en,
      ]
        .filter(Boolean)
        .join(" ");
    },
    student_display_name() {
      return inlineTranslator({
        en: this.student_name_en,
        th: this.student_name_th,
      });
    },
    faculty_prefix() {
      if (
        !this.student_data.prefix_name_en &&
        !this.student_data.prefix_name_th
      ) {
        return "";
      }
      return inlineTranslator({
        en: this.student_data.prefix_name_en,
        th: this.student_data.prefix_name_th,
      });
    },
  },
  watch: {
    student_id: {
      immediate: true,
      handler(studentId) {
        if (/^\d{8}$/.test(String(studentId || ""))) {
          this.fetch_data(studentId);
        } else {
          this.reset_data();
        }
      },
    },
  },
  methods: {
    inlineTranslator,
    objectTranslatorLowerCase,
    refresh_data() {
      if (/^\d{8}$/.test(String(this.student_id || ""))) {
        this.fetch_data(this.student_id);
      }
    },
    reset_data() {
      this.request_id += 1;
      this.loading = false;
      this.load_error = null;
      this.transaction_list = [];
      this.transaction_list_x = [];
      this.student_data = {};
      this.level_id = null;
      this.sub_level_id = null;
      this.student_note_dialog = false;
    },
    async fetch_data(studentId) {
      const currentRequestId = this.request_id + 1;
      this.request_id = currentRequestId;
      this.loading = true;
      this.load_error = null;
      this.transaction_list = [];
      this.transaction_list_x = [];
      this.student_data = {};
      this.level_id = null;
      this.sub_level_id = null;

      try {
        const studentData = await userService.getStudentData(studentId);
        if (currentRequestId !== this.request_id) return;

        const sourceLevelId = Number(studentData.level_id);
        const levelId = sourceLevelId === 1 ? 1 : 2;
        const subLevelId = Number(studentData.sub_level_id) === 1 ? 1 : null;

        const undergraduateRequest =
          levelId === 1
            ? this.fetch_payment_data(1, null, studentId)
            : Promise.resolve([]);
        const graduateRequest =
          (levelId === 1 && subLevelId === 1) || levelId !== 1
            ? this.fetch_payment_data(levelId, subLevelId, studentId)
            : Promise.resolve([]);

        const [undergraduateData, graduateData] = await Promise.all([
          undergraduateRequest,
          graduateRequest,
        ]);
        if (currentRequestId !== this.request_id) return;

        this.student_data = studentData;
        this.level_id = levelId;
        this.sub_level_id = subLevelId;
        this.transaction_list = undergraduateData;
        this.transaction_list_x = graduateData;
      } catch (error) {
        if (currentRequestId !== this.request_id) return;
        this.load_error = error.response?.data?.message || error.message;
        const errorMessage =
          typeof this.load_error === "object"
            ? this.objectTranslatorLowerCase(this.load_error, "message")
            : this.load_error;
        this.$toasted.error(errorMessage || this.$t("payment.load_error"), {
          duration: 0,
        });
      } finally {
        if (currentRequestId === this.request_id) this.loading = false;
      }
    },
    async fetch_payment_data(levelId, subLevelId, studentId) {
      const response = await payInSlipService.getPaymentData({
        level_id: levelId,
        student_id: studentId,
        sub_level_id: subLevelId,
      });

      // รองรับ response ทั้งแบบ { data: [] }, array โดยตรง และ response ที่ถูก API gateway ห่อ payload
      // พร้อมป้องกันไม่ให้หน้าแสดง JavaScript error "reading 'map'" เมื่อ response ผิดรูปแบบ
      const responseData = Array.isArray(response)
        ? response
        : Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response?.payload?.data)
            ? response.payload.data
            : Array.isArray(response?.data?.data)
              ? response.data.data
              : null;
      if (!responseData) {
        throw new Error(this.$t("payment.invalid_payment_data"));
      }

      const transactionList = responseData.map((item) => {
        if (item.regis_type) {
          const regisType = getById(
            this.type_of_regis,
            item.regis_type,
            "regis_type"
          );
          if (regisType) {
            item.regis_type_txt_th = regisType.regis_type_txt_th;
            item.regis_type_txt_en = regisType.regis_type_txt_en;
          }
        }

        item.tuition_fee = this.currency_converter(item.tuition_fee);
        item.special_fee2 = this.currency_converter(item.special_fee2);
        item.thesis_fee = this.currency_converter(item.thesis_fee);
        item.compre_fee = this.currency_converter(item.compre_fee);
        item.qe_fee = this.currency_converter(item.qe_fee);
        item.sum_other_fee = this.currency_converter(item.sum_other_fee);
        item.late_charge = this.currency_converter(item.late_charge);
        item.late_charge_original =
          item.late_charge_original == null
            ? item.late_charge
            : this.currency_converter(item.late_charge_original);
        item.pay_charge = this.currency_converter(item.pay_charge);
        item.paid = this.currency_converter(item.paid);
        item.remain = this.currency_converter(item.remain);
        item.error = null;
        return item;
      });

      for (const item of transactionList) {
        if (item.remain !== "0.00" && this.should_check_payment_condition) {
          try {
            const paymentTuitionFee = await payInSlipService.getPaymentTuitionFee(
              levelId,
              item.year,
              item.semester,
              studentId,
              subLevelId,
              this.use_student_payment_condition
            );
            if (
              this.use_student_payment_condition &&
              paymentTuitionFee.payment_condition_mode !== "student"
            ) {
              item.error = this.$t("payment.student_mode_unavailable");
            }
          } catch (error) {
            item.error =
              error.response?.data?.message ||
              error.message ||
              this.$t("payment.load_error");
          }
        }
      }

      return transactionList;
    },
    currency_converter(value) {
      if (Number(value) === 0) return "0.00";
      return Math.abs(value).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
  },
};
</script>

<style scoped>
.student-payment-summary {
  padding: 6px 12px;
  color: #555;
  border-radius: 4px;
}
.student-summary-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  column-gap: 72px;
  width: min(100%, 980px);
  margin: 0 auto;
}
.student-summary-item {
  display: flex;
  align-items: center;
  gap: 6px;
  min-height: 30px;
  padding-top: 3px !important;
  padding-bottom: 3px !important;
  line-height: 1.25;
  text-align: left;
}
.student-summary-label {
  color: #444;
  font-weight: 600;
  white-space: nowrap;
}
@media (max-width: 600px) {
  .student-payment-summary {
    padding: 6px 8px;
  }
  .student-summary-grid {
    grid-template-columns: 1fr;
    row-gap: 2px;
  }
  .student-summary-item {
    align-items: flex-start;
  }
}
</style>
